/**
 * LiveTestingQuestionField 컴포넌트
 *
 * inputType에 따라 적절한 V2Qs* 컴포넌트를 렌더링
 * Live Testing Preview에서 개별 질문의 입력 필드를 담당
 */

import { getCountriesOptions } from '@cyren/common-lib';
import { memo, useCallback, useMemo, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { QuestionImageDto, QuestionResponseDto } from '../../../../api/generated/types';
import { useTranslations } from '../../../../../patient/hooks/use-translations';
import { getLocaleCode } from '../../../organization/configurations/language/constants';
import {
  useStaticLocales,
  type SystemLocaleCodes,
  type SystemLocaleKeys,
} from '../../../../hooks/use-static-locales';
import LiveTestingContainer from './LiveTestingContainer';
import { LiveTestingAnswerValue, isSelectType } from './types';

// V2Qs 컴포넌트 import (named exports)
import { V2QsAbdLocation } from '../../../../shared/components/question-types/V2QsAbdLocation';
import {
  V2QsBloodPressure,
  V2QsBloodPressureValue,
} from '../../../../shared/components/question-types/V2QsBloodPressure';
import {
  V2QsBodySelect,
  V2QsBodySelectGroup,
} from '../../../../shared/components/question-types/V2QsBodySelect';
import { V2QsDate } from '../../../../shared/components/question-types/V2QsDate';
import { V2QsDateDob } from '../../../../shared/components/question-types/V2QsDateDob';
import { V2QsDatetime } from '../../../../shared/components/question-types/V2QsDatetime';
import {
  V2QsFrequency,
  V2QsFrequencyValue,
} from '../../../../shared/components/question-types/V2QsFrequency';
import { V2QsMultiText } from '../../../../shared/components/question-types/V2QsMultiText';
import { V2QsNumber } from '../../../../shared/components/question-types/V2QsNumber';
import { V2QsOnset, V2QsOnsetSimple } from '../../../../shared/components/question-types/V2QsOnset';
import { V2QsPhoneNumber } from '../../../../shared/components/question-types/V2QsPhoneNumber';
import { V2QsRange } from '../../../../shared/components/question-types/V2QsRange';
import { V2QsEqVas } from '../../../../shared/components/question-types/V2QsEqVas';
import {
  V2QsRelativeOnset,
  V2QsRelativeOnsetValue,
  V2QsRelativeOnsetUnit,
} from '../../../../shared/components/question-types/V2QsRelativeOnset';
import {
  V2QsSelect,
  V2QsSelectOption,
  V2QsSelectValue,
} from '../../../../shared/components/question-types/V2QsSelect';
import {
  V2QsSelectSheet,
  V2QsSelectSheetValue,
} from '../../../../shared/components/question-types/V2QsSelectSheet';
import { V2QsSelectCountry } from '../../../../shared/components/question-types/V2QsSelectCountry';
import { V2QsSignature } from '../../../../shared/components/question-types/V2QsSignature';
import { V2QsStageMileMarker } from '../../../../shared/components/question-types/V2QsStageMileMarker';
import { V2QsText } from '../../../../shared/components/question-types/V2QsText';
import { V2QsTextarea } from '../../../../shared/components/question-types/V2QsTextarea';
import { V2QsTime } from '../../../../shared/components/question-types/V2QsTime';
import {
  V2QsSxCollectIntelligent,
  V2QsSxCollectIntelligentValue,
} from '../../../../shared/components/question-types/V2QsSxCollectIntelligent';
import {
  V2QsSxAssociatedCollector,
  V2QsSxAssociatedCollectorValue,
} from '../../../../shared/components/question-types/V2QsSxAssociatedCollector';
import {
  V2QsMultiTextNested,
  V2QsMultiTextNestedTag,
  V2QsNestedQuestion,
} from '../../../../shared/components/question-types/V2QsMultiTextNested';
import LiveTestingSxSelectField from './LiveTestingSxSelectField';
import LiveTestingSxCollectField from './LiveTestingSxCollectField';
import LiveTestingCollapsibleField from './LiveTestingCollapsibleField';
import {
  genEqVasTopLabelKey,
  genEqVasBottomLabelKey,
  genEqVasValueBoxLabelKey,
  genScaleLabelKey,
} from '../questions/useQuestionConfigureModal';
import LiveTestingBodySelectField, { BodySelectValue } from './LiveTestingBodySelectField';
import LiveTestingHeadSelectField from './LiveTestingHeadSelectField';
import LiveTestingBodySelectTreeField, {
  BodySelectTreeValue,
} from './LiveTestingBodySelectTreeField';
import LiveTestingSxCollectAssociatedField from './LiveTestingSxCollectAssociatedField';
import type { CollapsibleValue } from './useLiveTestingCollapsible';
import { V2QsImage } from '../../../../shared/components/question-types/V2QsImage';
import LiveTestingDrawBodyField, { DrawBodyValue } from './LiveTestingDrawBodyField';
import {
  normalizeQuestionLabelForSurveyLocale,
  normalizeTranslatedLabelForSurveyLocale,
} from '../../../../shared/utils/survey-locale-text';
import { collectNestedQuestionTranslationEntries } from '../../../../shared/utils/nested-question-translation';

// 복부 위치 선택용 배경 이미지 (V1 QsAbdLocation과 동일한 이미지 사용)
import abdomenImg from '../../../../../report/components/assets/abdomen.png';

/** Convert raw key to human-readable label to avoid [번역 없음] while translations load */
function humanizeKey(key: string): string {
  return key
    .replace(/^(sx-ih-|sx-ai-|sx-|o-|q-|st-|stage-)/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// "None of the above" 키 상수
const NONE_ABOVE_KEY = 'o-none-above';

const normalizeComparableText = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

const resolveKnownNestedQuestionStaticKey = (
  questionText?: string | null,
): SystemLocaleKeys | null => {
  const normalized = normalizeComparableText(questionText || '');
  if (!normalized) {
    return null;
  }

  if (/\bwhat reaction do you have to(?: this)? medication\b/.test(normalized)) {
    return 'label-what-reaction-do-you-have';
  }

  return null;
};

function getImageUrl(image?: QuestionImageDto): string | null {
  if (!image) return null;

  const formats = image.formats as Record<string, { url?: string }> | undefined;
  if (formats?.medium?.url) return formats.medium.url;
  if (formats?.small?.url) return formats.small.url;
  if (formats?.thumbnail?.url) return formats.thumbnail.url;

  return image.url || null;
}

interface LiveTestingQuestionFieldProps {
  /** 질문 데이터 */
  question: QuestionResponseDto;
  /** 현재 답변 값 */
  value: LiveTestingAnswerValue;
  /** 값 변경 핸들러 */
  onChange: (value: LiveTestingAnswerValue) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** Chief complaint (optional) */
  chiefComplaint?: string;
}

/**
 * inputType에 따른 V2Qs 컴포넌트 렌더링
 */
function LiveTestingQuestionField({
  question,
  value,
  onChange,
  disabled = false,
  chiefComplaint,
}: LiveTestingQuestionFieldProps) {
  const { inputType: rawInputType, answerKeys, answerOptionsMap, typeOptions } = question;
  const backgroundImageUrl = useMemo(() => getImageUrl(question.images?.[0]), [question.images]);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  let inputType = rawInputType;
  if (rawInputType === 'select_sheet') {
    const isMulti = (typeOptions as { multi?: boolean })?.multi === true;
    inputType = isMulti ? 'select_sheet_multi' : 'select_sheet_single';
  }

  // Stage Milemarker용 stages, 전화번호 인증, 성별 가져오기
  const {
    stageMarkerStages,
    phoneVerification,
    visitUuid,
    surveyId,
    currentLocale,
    isMale,
    onSkipQuestion,
  } = LiveTestingContainer.useContainer();
  const localeCode = useMemo(
    () => getLocaleCode(currentLocale || 'english') as SystemLocaleCodes,
    [currentLocale],
  );
  const { sltStr } = useStaticLocales({ localeCode });
  const [, { t }] = useTranslations({ locale: currentLocale });
  const tr = useCallback(
    (key: string, fallbackValue: string, staticKey?: SystemLocaleKeys) => {
      if (staticKey) {
        const staticTranslated = sltStr({ key: staticKey });
        if (staticTranslated && staticTranslated !== staticKey) {
          return staticTranslated;
        }
      }
      const translated = t(key, null, { fallbackValue: null });
      if (translated && translated !== key) {
        return translated;
      }
      return fallbackValue;
    },
    [sltStr, t],
  );

  const possibleSymptoms = useMemo(() => {
    if (!answerKeys || inputType !== 'sx_collect_intelligent') return [];
    return answerKeys.filter((key) => key && key !== NONE_ABOVE_KEY && !key.startsWith('q-'));
  }, [answerKeys, inputType]);

  if (process.env.NODE_ENV === 'development' && inputType === 'sx_collect_intelligent') {
    // eslint-disable-next-line no-console
    console.log('[sx_collect_intelligent] field render', {
      questionKey: question.questionKey,
      inputType,
      answerKeyCount: answerKeys?.length || 0,
      possibleSymptomsCount: possibleSymptoms.length,
      visitUuid,
      chiefComplaint: (chiefComplaint || '').trim(),
      disabled,
    });
  }

  // Select 타입용 옵션 변환
  const selectOptions = useMemo<V2QsSelectOption[]>(() => {
    if (!isSelectType(inputType)) return [];

    const optionKeys = (answerKeys && answerKeys.length > 0)
      ? answerKeys
      : Object.keys(answerOptionsMap || {});
    if (optionKeys.length === 0) return [];

    return optionKeys.map((key) => {
      const optionData = answerOptionsMap?.[key];
      return {
        key,
        label: normalizeTranslatedLabelForSurveyLocale({
          locale: currentLocale,
          label: optionData?.label as string | undefined,
          fallbackKey: humanizeKey(key),
        }),
        requireNote: Boolean(optionData?.requireNote),
        mutualExclusive: Boolean(optionData?.mutuallyExclusive),
        disabled: false,
      };
    });
  }, [inputType, answerKeys, answerOptionsMap, currentLocale]);

  // Select 값 핸들러
  const handleSelectChange = useCallback(
    (newValue: V2QsSelectValue) => {
      onChange(newValue as LiveTestingAnswerValue);
    },
    [onChange],
  );

  // Text 값 핸들러
  const handleTextChange = useCallback(
    (newValue: string) => {
      onChange(newValue);
    },
    [onChange],
  );

  // Number 값 핸들러
  const handleNumberChange = useCallback(
    (newValue: number | undefined) => {
      onChange(newValue);
    },
    [onChange],
  );

  // BloodPressure 값 핸들러
  const handleBloodPressureChange = useCallback(
    (newValue: V2QsBloodPressureValue) => {
      onChange(newValue as LiveTestingAnswerValue);
    },
    [onChange],
  );

  // Frequency 값 핸들러
  const handleFrequencyChange = useCallback(
    (newValue: V2QsFrequencyValue) => {
      onChange(newValue as LiveTestingAnswerValue);
    },
    [onChange],
  );

  // BodySelect 값 핸들러
  const handleBodySelectChange = useCallback(
    (newValue: V2QsBodySelectGroup[]) => {
      onChange(newValue as unknown as LiveTestingAnswerValue);
    },
    [onChange],
  );

  // RelativeOnset 값 핸들러
  const handleRelativeOnsetChange = useCallback(
    (newValue: V2QsRelativeOnsetValue) => {
      onChange(newValue as LiveTestingAnswerValue);
    },
    [onChange],
  );

  // MultiTextNested용 modal state
  const [openTagIndex, setOpenTagIndex] = useState<number | null>(null);

  // MultiTextNested용 nestedQuestions 변환
  const nestedQuestions = useMemo<V2QsNestedQuestion[]>(() => {
    if (inputType !== 'multi_text_nested' || !answerOptionsMap) return [];

    const entries = collectNestedQuestionTranslationEntries({
      questionKey: question.questionKey || '__nested__',
      answerOptionsMap: answerOptionsMap as Record<string, any>,
    });

    return entries.map((entry) => ({
      question: (() => {
        const translatedFromEntryKey = t(entry.key, null, { fallbackValue: null });
        if (translatedFromEntryKey && translatedFromEntryKey !== entry.key) {
          return normalizeTranslatedLabelForSurveyLocale({
            locale: currentLocale,
            label: translatedFromEntryKey,
            fallbackKey: undefined,
          });
        }

        const knownStaticKey = resolveKnownNestedQuestionStaticKey(entry.question);
        if (knownStaticKey) {
          const staticTranslated = sltStr({ key: knownStaticKey });
          if (staticTranslated && staticTranslated !== knownStaticKey) {
            return staticTranslated;
          }

          const translatedFromMappedKey = t(knownStaticKey, null, { fallbackValue: null });
          if (translatedFromMappedKey && translatedFromMappedKey !== knownStaticKey) {
            return translatedFromMappedKey;
          }
        }

        return normalizeTranslatedLabelForSurveyLocale({
          locale: currentLocale,
          label: entry.question,
          fallbackKey: undefined,
        });
      })(),
      options: entry.optionKeys
        .map((key) => {
          const option = (answerOptionsMap as Record<string, any>)[key];
          return {
            key,
            label: normalizeTranslatedLabelForSurveyLocale({
              locale: currentLocale,
              label: option?.label,
              fallbackKey: humanizeKey(key),
            }),
            requireNote: option?.requireNote || false,
            mutuallyExclusive: option?.mutuallyExclusive || false,
            order: option?.order || 0,
          };
        })
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(({ key, label, requireNote, mutuallyExclusive }) => ({
          key,
          label,
          requireNote,
          mutuallyExclusive,
        })),
    }));
  }, [inputType, answerOptionsMap, currentLocale, question.questionKey, sltStr, t]);

  // MultiTextNested 값 핸들러
  const handleMultiTextNestedChange = useCallback(
    (newValue: V2QsMultiTextNestedTag[]) => {
      onChange(newValue as unknown as LiveTestingAnswerValue);
    },
    [onChange],
  );

  // 기본 props - 모든 입력 필드가 100% 너비를 가지도록 fullWidth 적용
  const commonProps = {
    disabled,
    error: false,
    fullWidth: true,
  };
  const localizedUnitOptions = useMemo(() => {
    const stripDurationToken = (label: string) => label.replace(/\{\{v0\}\}\s*/g, '');
    return [
      {
        value: 'minute',
        labelSingular: stripDurationToken(tr('minute ago', 'minute ago', 'minute ago')),
        labelPlural: stripDurationToken(tr('minutes ago', 'minutes ago', 'minutes ago')),
      },
      {
        value: 'hour',
        labelSingular: stripDurationToken(tr('hour ago', 'hour ago', 'hour ago')),
        labelPlural: stripDurationToken(tr('hours ago', 'hours ago', 'hours ago')),
      },
      {
        value: 'day',
        labelSingular: stripDurationToken(tr('day ago', 'day ago', 'day ago')),
        labelPlural: stripDurationToken(tr('days ago', 'days ago', 'days ago')),
      },
      {
        value: 'week',
        labelSingular: stripDurationToken(tr('week ago', 'week ago', 'week ago')),
        labelPlural: stripDurationToken(tr('weeks ago', 'weeks ago', 'weeks ago')),
      },
      {
        value: 'month',
        labelSingular: stripDurationToken(tr('month ago', 'month ago', 'month ago')),
        labelPlural: stripDurationToken(tr('months ago', 'months ago', 'months ago')),
      },
      {
        value: 'year',
        labelSingular: stripDurationToken(tr('year ago', 'year ago', 'year ago')),
        labelPlural: stripDurationToken(tr('years ago', 'years ago', 'years ago')),
      },
    ];
  }, [tr]);
  const formatRelativeOnsetDate = useCallback(
    (date: Date, unit: V2QsRelativeOnsetUnit) => {
      const locale = localeCode || 'en';

      switch (unit) {
        case 'minute':
        case 'hour':
          return new Intl.DateTimeFormat(locale, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            weekday: 'long',
            hour: '2-digit',
            hour12: true,
          }).format(date);
        case 'day':
        case 'week':
          return new Intl.DateTimeFormat(locale, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            weekday: 'long',
          }).format(date);
        case 'month':
          return new Intl.DateTimeFormat(locale, {
            month: 'long',
            year: 'numeric',
          }).format(date);
        case 'year':
          return new Intl.DateTimeFormat(locale, {
            year: 'numeric',
          }).format(date);
        default:
          return new Intl.DateTimeFormat(locale, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            weekday: 'long',
          }).format(date);
      }
    },
    [localeCode],
  );
  const notePlaceholder = tr(
    'label-describe-in-detail',
    'Describe in detail',
    'label-describe-in-detail',
  );
  const noteRequiredMessage = tr(
    'label-detailed-description-required',
    'Detailed description required',
    'label-detailed-description-required',
  );

  // inputType별 컴포넌트 렌더링
  switch (inputType) {
    // Text 입력 계열
    case 'text':
      return (
        <V2QsText
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={tr('placeholder-enter-text', 'Enter text', 'placeholder-enter-text')}
        />
      );

    case 'textarea':
      return (
        <V2QsTextarea
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={tr('placeholder-enter-text', 'Enter text', 'placeholder-enter-text')}
          minLength={typeOptions?.min}
          maxLength={typeOptions?.max}
        />
      );

    case 'number':
      return (
        <V2QsNumber
          {...commonProps}
          value={(value as number) ?? undefined}
          onChange={handleNumberChange}
          min={typeOptions?.min}
          max={typeOptions?.max}
        />
      );

    case 'number_unit_select': {
      // answerKeys에서 단위 옵션 추출 (QuestionCard와 동일한 패턴)
      const unitOptions =
        answerKeys?.map((key) => {
          const optionData = answerOptionsMap?.[key] as Record<string, unknown> | undefined;
          return {
            key,
            label: normalizeTranslatedLabelForSurveyLocale({
              locale: currentLocale,
              label: optionData?.label as string | undefined,
              fallbackKey: humanizeKey(key),
            }),
          };
        }) || [];

      // 단위 옵션이 없으면 "Missing an answer" 표시 (V1과 동일)
      if (unitOptions.length === 0) {
        return (
          <div className='text-error font-medium'>
            {tr('label-missing-answer', 'Missing an answer', 'label-missing-answer')}
          </div>
        );
      }

      // 값 구조: { value: number, unit: string }
      const numberUnitValue = value as { value?: number; unit?: string } | undefined;

      return (
        <V2QsNumber
          {...commonProps}
          value={numberUnitValue?.value}
          onChange={(newValue) => {
            onChange({
              value: newValue,
              unit: numberUnitValue?.unit || unitOptions[0]?.key,
            } as unknown as LiveTestingAnswerValue);
          }}
          units={unitOptions}
          selectedUnit={numberUnitValue?.unit || unitOptions[0]?.key}
          onUnitChange={(newUnit) => {
            onChange({
              value: numberUnitValue?.value,
              unit: newUnit,
            } as unknown as LiveTestingAnswerValue);
          }}
          min={typeOptions?.min}
          max={typeOptions?.max}
        />
      );
    }

    case 'phone_number': {
      // verifyPhoneNumber 옵션: 'required' | 'optional' | 'disabled'
      // 'disabled'가 아니면 인증 버튼 표시
      const verifyOption = (typeOptions as { verifyPhoneNumber?: string })?.verifyPhoneNumber;
      const showVerification = verifyOption !== 'disabled';

      return (
        <V2QsPhoneNumber
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          showVerification={showVerification}
          isVerified={phoneVerification.isVerified(question.questionKey)}
          isVerifying={phoneVerification.isVerifying(question.questionKey)}
          onVerifyClick={() => phoneVerification.handleVerify(question.questionKey)}
        />
      );
    }

    // 선택 계열 - 단일 선택 (radio 모드)
    case 'select':
    case 'select_v2':
    case 'radio':
      return (
        <V2QsSelect
          {...commonProps}
          mode='radio'
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );

    // select_sheet - 옵션 수에 따라 컴포넌트 결정 (deprecated - use select_sheet_single or select_sheet_multi)
    // V1과 동일: 옵션 > 4개 → V2QsSelectSheet (바텀시트), multi 여부와 상관없이
    // PC에서는 항상 인라인 표시
    case 'select_sheet': {
      const isMulti = (typeOptions as { multi?: boolean })?.multi === true;
      const forceDropdown = (typeOptions as { forceDropdown?: boolean })?.forceDropdown === true;
      const useLengthyDropdown = !isDesktop && (forceDropdown || selectOptions.length > 4);

      if (useLengthyDropdown) {
        return (
          <V2QsSelectSheet
            options={selectOptions}
            value={(value as V2QsSelectSheetValue) || { selectedKeys: [], notesForKeys: {} }}
            onChange={(v: V2QsSelectSheetValue) => onChange(v as unknown as LiveTestingAnswerValue)}
            single={!isMulti}
            placeholder={tr('label-select', 'Select', 'label-select')}
            closeButtonText={tr('btn-close', 'Close', 'btn-close')}
            notePlaceholder={notePlaceholder}
            noteRequiredMessage={noteRequiredMessage}
            disabled={disabled}
          />
        );
      }

      return (
        <V2QsSelect
          {...commonProps}
          mode={isMulti ? 'checkbox' : 'radio'}
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );
    }

    // select_sheet_single - 단일 선택 (radio 또는 single dropdown)
    // PC에서는 항상 인라인 표시
    case 'select_sheet_single': {
      const forceDropdown = (typeOptions as { forceDropdown?: boolean })?.forceDropdown === true;
      const useLengthyDropdown = !isDesktop && (forceDropdown || selectOptions.length > 4);

      if (useLengthyDropdown) {
        return (
          <V2QsSelectSheet
            options={selectOptions}
            value={(value as V2QsSelectSheetValue) || { selectedKeys: [], notesForKeys: {} }}
            onChange={(v: V2QsSelectSheetValue) => onChange(v as unknown as LiveTestingAnswerValue)}
            single
            placeholder={tr('label-select', 'Select', 'label-select')}
            closeButtonText={tr('btn-close', 'Close', 'btn-close')}
            notePlaceholder={notePlaceholder}
            noteRequiredMessage={noteRequiredMessage}
            disabled={disabled}
          />
        );
      }

      return (
        <V2QsSelect
          {...commonProps}
          mode='radio'
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );
    }

    // select_sheet_multi - 다중 선택 (checkbox 또는 multi dropdown)
    // PC에서는 항상 인라인 표시
    case 'select_sheet_multi': {
      const forceDropdown = (typeOptions as { forceDropdown?: boolean })?.forceDropdown === true;
      const useLengthyDropdown = !isDesktop && (forceDropdown || selectOptions.length > 4);

      if (useLengthyDropdown) {
        return (
          <V2QsSelectSheet
            options={selectOptions}
            value={(value as V2QsSelectSheetValue) || { selectedKeys: [], notesForKeys: {} }}
            onChange={(v: V2QsSelectSheetValue) => onChange(v as unknown as LiveTestingAnswerValue)}
            placeholder={tr('label-select', 'Select', 'label-select')}
            closeButtonText={tr('btn-close', 'Close', 'btn-close')}
            notePlaceholder={notePlaceholder}
            noteRequiredMessage={noteRequiredMessage}
            disabled={disabled}
          />
        );
      }

      return (
        <V2QsSelect
          {...commonProps}
          mode='checkbox'
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );
    }

    // 다중 선택 (checkbox 모드)
    case 'multiChoice':
      return (
        <V2QsSelect
          {...commonProps}
          mode='checkbox'
          options={selectOptions}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          notePlaceholder={notePlaceholder}
        />
      );

    // 증상 선택 (sx_select) - V1 QsSxCollector의 V2 버전
    // 옵션 클릭 시 해당 증상 트리의 질문들을 모달에서 표시
    case 'sx_select':
      return (
        <LiveTestingSxSelectField
          question={question}
          value={(value as V2QsSelectValue) || { selectedKeys: [], notesForKeys: {} }}
          onChange={handleSelectChange}
          disabled={disabled}
        />
      );

    // 접이식 섹션 (collapsible) - V1 QsCollapsible의 V2 버전
    // 그룹별로 접이식 UI로 증상 아이템 표시
    case 'collapsible':
      return (
        <LiveTestingCollapsibleField
          question={question}
          value={(value as CollapsibleValue) || { selectedKeys: [], itemAnswers: {} }}
          onChange={(v) => onChange(v as LiveTestingAnswerValue)}
          disabled={disabled}
        />
      );

    // 날짜/시간 계열
    case 'date':
      return (
        <V2QsDate
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={tr('label-select', 'Select...', 'label-select')}
        />
      );

    case 'date_dob':
      return (
        <V2QsDateDob
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={tr('mon-day-year', 'Month / Day / Year', 'mon-day-year')}
          hint={tr('mon-day-year', 'Month / Day / Year', 'mon-day-year')}
          ageRestrictionCloseText={tr('btn-close', 'Close', 'btn-close')}
        />
      );

    case 'datetime':
      return (
        <V2QsDatetime
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={tr('label-select', 'Select...', 'label-select')}
        />
      );

    case 'time':
      return (
        <V2QsTime
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
          placeholder={tr('label-select', 'Select...', 'label-select')}
        />
      );

    // 특수 입력 계열
    case 'range':
      return (
        <V2QsRange
          {...commonProps}
          value={(value as number) ?? typeOptions?.min ?? 0}
          onChange={(v: number) => onChange(v)}
          min={typeOptions?.min ?? 0}
          max={typeOptions?.max ?? 10}
          step={typeOptions?.step ?? 1}
          labels={(() => {
            const vals: number[] = question.typeOptions?.scaleLabels || [];
            return vals
              .map((val) => {
                const text = tr(genScaleLabelKey(question.questionKey, val), '');
                return text ? { value: val, text } : null;
              })
              .filter(Boolean) as Array<{ value: number; text: string }>;
          })()}
        />
      );

    // PROM 타입: EQ-VAS (0~100 세로 눈금자)
    case 'eq_vas': {
      const eqVasLabel = normalizeQuestionLabelForSurveyLocale({
        locale: currentLocale,
        label: question.label,
      });
      const qKey = question.questionKey;
      return (
        <V2QsEqVas
          value={(value as number) ?? undefined}
          onChange={(v: number) => onChange(v)}
          topLabel={tr(genEqVasTopLabelKey(qKey), '')}
          bottomLabel={tr(genEqVasBottomLabelKey(qKey), '')}
          valueBoxLabel={tr(genEqVasValueBoxLabelKey(qKey), '')}
          title={
            <span>
              {eqVasLabel}
              {!question.optional && ' *'}
            </span>
          }
          leftText={(() => {
            const raw = question.descriptionInline || (typeOptions as Record<string, unknown> | undefined)?.leftText as string | undefined;
            if (!raw) return undefined;
            // 번역 키 형태(영소문자+숫자+하이픈만)이면 미번역 → 빈 값
            if (/^[a-z0-9][-a-z0-9]*$/.test(raw)) return undefined;
            return raw;
          })()}
          disabled={disabled}
        />
      );
    }

    case 'blood_pressure':
      return (
        <V2QsBloodPressure
          {...commonProps}
          value={(value as V2QsBloodPressureValue) || {}}
          onChange={handleBloodPressureChange}
          systolicPlaceholder={tr('qs-bp-systolic', 'Systolic', 'qs-bp-systolic')}
          diastolicPlaceholder={tr('qs-bp-diastolic', 'Diastolic', 'qs-bp-diastolic')}
        />
      );

    case 'frequency':
      return (
        <V2QsFrequency
          {...commonProps}
          value={(value as V2QsFrequencyValue) || {}}
          onChange={handleFrequencyChange}
        />
      );

    case 'signature':
      return (
        <V2QsSignature
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
        />
      );

    // 위치 선택 계열 - 트리 기반 body_select (V1 QsInputBodySelectTree와 동일)
    case 'body_select':
    case 'body_select_whole':
      return (
        <LiveTestingBodySelectTreeField
          question={question}
          value={(value as BodySelectTreeValue) || { selectedKeyGroups: [] }}
          onChange={(v) => onChange(v as unknown as LiveTestingAnswerValue)}
          disabled={disabled}
        />
      );

    case 'abd_location':
    case 'select_abd_location':
      return (
        <V2QsAbdLocation
          {...commonProps}
          value={(value as string[]) || []}
          onChange={(v: string[]) => onChange(v)}
          abdomenImageUrl={abdomenImg}
        />
      );

    // Body Selection - Head (체크박스 리스트 UI)
    case 'body_select_head':
      return (
        <LiveTestingHeadSelectField
          question={question}
          value={(value as BodySelectValue) || { answerKeys: [] }}
          onChange={(v) => onChange(v as unknown as LiveTestingAnswerValue)}
          disabled={disabled}
          locale={currentLocale}
        />
      );

    // Body Selection 서브타입들 (SVG 클릭 방식)
    case 'body_select_belly':
    case 'body_select_chest':
    case 'body_select_back':
    case 'body_select_face':
    case 'body_select_limbs':
    case 'body_select_mouth':
    case 'body_select_dental':
    case 'body_select_sensory':
    case 'body_select_subpart':
    case 'body_select_arm':
    case 'body_select_leg':
    case 'body_select_joints':
      return (
        <LiveTestingBodySelectField
          question={question}
          value={(value as BodySelectValue) || { answerKeys: [] }}
          onChange={(v) => onChange(v as unknown as LiveTestingAnswerValue)}
          disabled={disabled}
          isMale
        />
      );

    // 복합 입력 계열
    case 'multi_text':
      return (
        <V2QsMultiText
          {...commonProps}
          value={(value as string[]) || []}
          onChange={(v: string[]) => onChange(v)}
          placeholder={tr(
            'v2-qs-multi-text-nested-placeholder',
            'Type and add',
            'v2-qs-multi-text-nested-placeholder',
          )}
          addButtonText={tr('v2-qs-multi-text-nested-add', 'ADD', 'v2-qs-multi-text-nested-add')}
        />
      );

    case 'multi_text_nested':
      return (
        <V2QsMultiTextNested
          value={(value as V2QsMultiTextNestedTag[]) || []}
          onChange={handleMultiTextNestedChange}
          nestedQuestions={nestedQuestions}
          openTagIndex={openTagIndex}
          onOpenModal={(index) => setOpenTagIndex(index)}
          onCloseModal={() => setOpenTagIndex(null)}
          disabled={disabled}
          min={typeOptions?.min}
          max={typeOptions?.max}
          placeholder={tr(
            'v2-qs-multi-text-nested-placeholder',
            'Type and add...',
            'v2-qs-multi-text-nested-placeholder',
          )}
          addButtonText={tr('v2-qs-multi-text-nested-add', 'ADD', 'v2-qs-multi-text-nested-add')}
          selectButtonText={tr('label-select', 'Select', 'label-select')}
          closeButtonText={tr('btn-close', 'Close', 'btn-close')}
          minErrorTemplate={tr(
            'qs-multi-min-error',
            'Please enter at least {min} item(s).',
            'qs-multi-min-error',
          )}
          maxErrorTemplate={tr(
            'qs-multi-max-error',
            'You can enter up to {max} item(s) only.',
            'qs-multi-max-error',
          )}
        />
      );

    // onset: 간단한 날짜/시간 선택기 + "X hours/days" 표시 (V1 QsInputOnset)
    case 'onset':
      return (
        <V2QsOnsetSimple
          {...commonProps}
          value={(value as string) || ''}
          onChange={handleTextChange}
        />
      );

    // onset_v2: 프리셋 버튼 "In hours/days/weeks..." + 날짜/시간 조정 (V1 QsInputOnsetV2)
    case 'onset_v2':
      return (
        <V2QsOnset {...commonProps} value={(value as string) || ''} onChange={handleTextChange} />
      );

    case 'relative_onset':
    case 'datetime_relative_onset':
      return (
        <V2QsRelativeOnset
          {...commonProps}
          value={(value as V2QsRelativeOnsetValue) || {}}
          onChange={handleRelativeOnsetChange}
          unitOptions={localizedUnitOptions as any}
          formatCalculatedDate={formatRelativeOnsetDate}
        />
      );

    // 표시 전용 계열
    case 'stage_milemarker':
      // Stage Mile Marker - V1과 동일하게 stageBoundary step들을 표시
      return (
        <V2QsStageMileMarker
          title={normalizeQuestionLabelForSurveyLocale({
            locale: currentLocale,
            label: question.label,
          })}
          stages={stageMarkerStages}
        />
      );

    case 'markdown':
      // Markdown은 label에 포함된 내용을 표시 (별도 처리 필요 없음)
      return null;

    // 증상 수집 계열
    // V1과 동일: "⊕ symptoms" 버튼 클릭 시 모달에서 옵션 선택
    case 'sx_collect_intelligent': {
      return (
        <V2QsSxCollectIntelligent
          options={[]}
          possibleSymptoms={possibleSymptoms}
          answerKeys={answerKeys}
          answerOptionsMap={answerOptionsMap as Record<string, { label?: string }>}
          visitUuid={visitUuid}
          chiefComplaint={chiefComplaint}
          surveyId={surveyId ? Number(surveyId) : undefined}
          locale={currentLocale}
          suppressSxCollectors={
            (typeOptions as Record<string, unknown> | undefined)?.suppressSxCollectors === true
          }
          value={(value as V2QsSxCollectIntelligentValue) || { selectedKeys: [] }}
          onChange={(v: V2QsSxCollectIntelligentValue) =>
            onChange(v as unknown as LiveTestingAnswerValue)
          }
          disabled={disabled}
        />
      );
    }

    case 'sx_collect': {
      console.log('[LiveTestingQuestionField] sx_collect value:', value);
      return (
        <LiveTestingSxCollectField
          question={question}
          value={(value as V2QsSxCollectIntelligentValue) || { selectedKeys: [] }}
          onChange={(v: V2QsSxCollectIntelligentValue) =>
            onChange(v as unknown as LiveTestingAnswerValue)
          }
          disabled={disabled}
        />
      );
    }

    // 연관 증상 수집 - 이미 선택된 증상 트리들에서 연관 증상 추출
    case 'sx_collect_associated': {
      return (
        <LiveTestingSxCollectAssociatedField
          question={question}
          value={(value as V2QsSxCollectIntelligentValue) || { selectedKeys: [] }}
          onChange={(v: V2QsSxCollectIntelligentValue) =>
            onChange(v as unknown as LiveTestingAnswerValue)
          }
          disabled={disabled}
          onSkip={onSkipQuestion}
        />
      );
    }

    // 국가 선택 (단일)
    case 'country_select':
    case 'select_country': {
      const countryOptions = getCountriesOptions();
      return (
        <V2QsSelectCountry
          {...commonProps}
          options={countryOptions}
          value={(value as string) || ''}
          onChange={(v: string | string[]) => onChange(v as string)}
          placeholder={tr(
            'config-general-placeholder-country',
            'Select country',
            'config-general-placeholder-country',
          )}
        />
      );
    }

    // 국가 선택 (다중)
    case 'select_countries': {
      const countryOptions = getCountriesOptions();
      return (
        <V2QsSelectCountry
          {...commonProps}
          options={countryOptions}
          value={(value as string[]) || []}
          onChange={(v: string | string[]) => onChange(v as string[])}
          placeholder={tr(
            'config-general-placeholder-countries',
            'Select countries',
            'config-general-placeholder-countries',
          )}
          multi
        />
      );
    }

    // 이미지 업로드
    case 'image':
      return (
        <V2QsImage
          value={(value as string) || ''}
          onChange={(v: string) => onChange(v)}
          disabled={disabled}
          dropzoneText='Drag and drop or click to upload'
          fileTypeHint='JPEG, PNG, GIF, WebP (max 10MB)'
          hint='Upload an image for this question'
        />
      );

    // Draw on image (캔버스 드로잉)
    case 'draw_body':
      return (
        <LiveTestingDrawBodyField
          value={(value as DrawBodyValue) || {}}
          onChange={(v) => onChange(v as unknown as LiveTestingAnswerValue)}
          backgroundImageUrl={backgroundImageUrl}
          disabled={disabled}
        />
      );

    // 지원하지 않는 타입
    default:
      return (
        <div className='p-2 text-sm text-gray-500 bg-gray-100 rounded'>
          Unsupported input type: {inputType}
        </div>
      );
  }
}

export default memo(LiveTestingQuestionField);
